<template>
  <section class="hero">
    <!-- Ambient Background -->
    <div class="hero-bg">
      <div class="hero-gradient-1"></div>
      <div class="hero-gradient-2"></div>
      <div class="hero-dots"></div>
    </div>

    <div class="hero-content">
      <!-- Badge -->
      <div class="hero-badge">
        <span class="hero-badge-dot"></span>
        New wallpapers added daily
      </div>

      <h1 class="hero-title">
        Discover Amazing
        <span class="hero-title-accent">Wallpapers</span>
      </h1>

      <p class="hero-subtitle">
        Browse our curated collection of high-quality wallpapers, 
        crafted for every device you own.
      </p>

      <!-- Search-style CTA -->
      <div class="hero-actions">
        <BaseButton variant="primary" size="lg" @click="navigateToCategories" customClass="hero-btn-primary">
          <template #icon-left>
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          </template>
          Browse Categories
        </BaseButton>

        <BaseButton variant="outline" size="lg" @click="navigateToSearch" customClass="hero-btn-secondary">
          <template #icon-left>
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </template>
          Search
        </BaseButton>
      </div>

      <!-- Device Showcase (kept but with cleaner styling) -->
      <div class="hero-showcase">
        <DeviceShowcase 
          :devices="devices"
          mode="carousel"
          :auto-rotate="true"
          :rotate-interval="3000"
          :wallpaper-rotate-interval="5000"
          :show-labels="true"
          :show-controls="false"
          @device-click="handleDeviceClick"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { useRouter } from 'vue-router';
import { BaseButton, DeviceShowcase } from './ui';
import { useDeviceMockups } from '../composables/useDeviceMockups';

export default {
  name: 'HeroSection',
  components: {
    DeviceShowcase,
    BaseButton
  },
  setup() {
    const router = useRouter();
    const { getHeroCarouselDevices } = useDeviceMockups();

    // Get devices from the centralized composable
    const devices = getHeroCarouselDevices();

    const handleDeviceClick = (device) => {
      console.log(`Zooming ${device.type} device`);
    };

    const navigateToCategories = () => {
      router.push('/categories');
    };

    const navigateToSearch = () => {
      router.push('/search');
    };

    return {
      devices,
      handleDeviceClick,
      navigateToCategories,
      navigateToSearch
    };
  }
};
</script>

<style scoped>
/* ============================================
   HERO - Open, immersive, full-bleed
   ============================================ */

.hero {
  position: relative;
  overflow: hidden;
  padding: clamp(4rem, 10vh, 8rem) clamp(1rem, 4vw, 3rem) clamp(3rem, 8vh, 6rem);
}

/* Ambient gradients */
.hero-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
}

.hero-gradient-1 {
  position: absolute;
  width: 60vw;
  height: 60vw;
  max-width: 700px;
  max-height: 700px;
  top: -20%;
  right: -15%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(147, 51, 234, 0.08) 0%, transparent 70%);
  animation: float-slow 12s ease-in-out infinite alternate;
}

.hero-gradient-2 {
  position: absolute;
  width: 50vw;
  height: 50vw;
  max-width: 600px;
  max-height: 600px;
  bottom: -10%;
  left: -10%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(236, 72, 153, 0.06) 0%, transparent 70%);
  animation: float-slow 10s ease-in-out infinite alternate-reverse;
}

.hero-dots {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(circle, rgba(147, 51, 234, 0.06) 1px, transparent 1px);
  background-size: 32px 32px;
  opacity: 0.5;
}

.dark .hero-gradient-1 {
  background: radial-gradient(circle, rgba(147, 51, 234, 0.12) 0%, transparent 70%);
}

.dark .hero-gradient-2 {
  background: radial-gradient(circle, rgba(236, 72, 153, 0.08) 0%, transparent 70%);
}

.dark .hero-dots {
  background-image: radial-gradient(circle, rgba(147, 51, 234, 0.08) 1px, transparent 1px);
}

/* Content */
.hero-content {
  position: relative;
  z-index: 1;
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

/* Badge */
.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #9333ea;
  background: rgba(147, 51, 234, 0.08);
  padding: 0.4rem 1rem;
  border-radius: 9999px;
  margin-bottom: 1.5rem;
  animation: fade-in-up 0.6s ease forwards;
}

.dark .hero-badge {
  background: rgba(147, 51, 234, 0.15);
  color: #c084fc;
}

.hero-badge-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #9333ea;
  animation: dot-pulse 2s ease-in-out infinite;
}

/* Title */
.hero-title {
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 800;
  line-height: 1.1;
  color: #111827;
  margin-bottom: 1.25rem;
  animation: fade-in-up 0.6s ease 0.1s forwards;
  opacity: 0;
}

.dark .hero-title {
  color: #f9fafb;
}

.hero-title-accent {
  background: linear-gradient(135deg, #9333ea, #ec4899);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* Subtitle */
.hero-subtitle {
  font-size: clamp(1rem, 2vw, 1.25rem);
  color: #6b7280;
  max-width: 540px;
  line-height: 1.6;
  margin-bottom: 2.5rem;
  animation: fade-in-up 0.6s ease 0.2s forwards;
  opacity: 0;
}

.dark .hero-subtitle {
  color: #9ca3af;
}

/* Actions */
.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 3rem;
  animation: fade-in-up 0.6s ease 0.3s forwards;
  opacity: 0;
}

.hero-btn-primary {
  border-radius: 9999px !important;
  padding-left: 1.75rem !important;
  padding-right: 1.75rem !important;
}

.hero-btn-secondary {
  border-radius: 9999px !important;
  padding-left: 1.75rem !important;
  padding-right: 1.75rem !important;
  border-color: rgba(147, 51, 234, 0.3) !important;
}

.dark .hero-btn-secondary {
  border-color: rgba(147, 51, 234, 0.4) !important;
  color: #c084fc !important;
}

/* Device showcase area */
.hero-showcase {
  width: 100%;
  max-width: 900px;
  animation: fade-in-up 0.6s ease 0.4s forwards;
  opacity: 0;
}

/* Animations */
@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes float-slow {
  from { transform: translate(0, 0); }
  to { transform: translate(20px, -20px); }
}

@keyframes dot-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.8); }
}

/* Responsive */
@media (max-width: 640px) {
  .hero {
    padding: 3rem 1rem 2rem;
  }

  .hero-actions {
    flex-direction: column;
    width: 100%;
    max-width: 320px;
  }

  .hero-actions > * {
    width: 100%;
  }
}
</style>
